import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useModules } from '../../../utils/modules';
import moment from 'moment';

import {getManageUserList, updateUserData} from '../actions/action';
import DialogueBox     from '../../../components/DialogueBox';
import ImageCropper    from '../../../components/ImageCropper';
import ConfirmPrompt   from '../../../components/ConfirmationPrompt';
import LoaderComponent from '../.././../components/Loader';
import UserImage       from '../../../components/UserImage/index';
import Icon            from '../../../icons';
import { DEFAULT_DATE_FORMAT } from '../../../common/constants';
import { hasGuidValue } from '../../../common/util';

// Constants
const PHONE_REGEX   = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/;
const NAME_REGEX    = /^[a-zA-Z '-]+$/;
const EMPTY_NAME_ERRORS = { first: null, middle: null, last: null };
const NAME_FIELDS   = ['first', 'middle', 'last'];

// Helpers
const buildFullName = (data) => {
    const first  = (data?.first_name || data?.firstName  || '').trim();
    const middle = (data?.middle_name || data?.middleName || '').trim();
    const last   = (data?.last_name || data?.lastName   || '').trim();

    return [first, middle, last].filter(Boolean).join(' ');
};

const getSelectedTZDisplayValue = (tzValue, timezoneData) =>
    timezoneData?.find(val => val.mapped_time_zone === tzValue)?.display_value || '';

const hasSupervisor = (info) => Object.keys(info ?? {}).length > 0;

// Sub-components
const EditActions = ({ onSave, onCancel }) => (
    <div className="rowSpaceBetween companyList__profileBox__profileData__fullName">
        <button type="button" className="btn-cc btn-cc__transparent" onClick={onSave} aria-label="Save">
            <Icon name="check" className="icon activeIcon check" />
        </button>
        <button type="button" className="btn-cc btn-cc__transparent" onClick={onCancel} aria-label="Cancel">
            <Icon name="close" className="icon close" />
        </button>
    </div>
);

const ValidationField = ({ error, children }) => (
    <li className={`mr ${error ? 'errorMsg' : ''}`}>
        <div className="formGroup">{children}</div>
        {error && <span>{error}</span>}
    </li>
);

const ProfileRow = ({ label, testId, children }) => (
    <div className="companyList__profileBox__profileData" data-test={testId}>
        <label className="gridEven">{label}</label>
        <span className="gridOdd">{children}</span>
    </div>
);

const EditButton = ({ onClick, testId, ariaLabel }) => (
    <button
        type="button"
        className="btn-cc btn-cc__link icon ml__1 editDetail"
        onClick={onClick}
        data-test={testId}
        aria-label={ariaLabel || 'Edit'}
    >
        <Icon name="editActive" className="icon" />
    </button>
);

const Details = ({
    userView, timezoneData, isLoading,
    showToastMessage, orgData, supervisorInfo,
}) => {
    const dispatch = useDispatch();
    const { isModuleEnabled } = useModules();

    // Local States
    const [userName,               setUserName]               = useState({ first: '', middle: '', last: '' });
    const [phone,                  setPhone]                  = useState('');
    const [selectedTimeZone,       setSelectedTimeZone]       = useState('');
    const [validationErrors,       setValidationErrors]       = useState({ first: null, middle: null, last: null, phone: null });
    const [isEditName,             setIsEditName]             = useState(false);
    const [isEditPhone,            setIsEditPhone]            = useState(false);
    const [isEditTimezone,         setIsEditTimezone]         = useState(false);
    const [isModal,                setIsModal]                = useState(false);
    const [isConfirmation,         setIsConfirmation]         = useState(false);
    const [isSupervisor,           setIsSupervisor]           = useState(false);
    const [prevSupervisorInfo,     setPrevSupervisorInfo]     = useState({});
    const [hasUserImage,           setHasUserImage]           = useState(false);
    
    // Derived
    const companyName = userView?.orgId != null && String(userView.orgId) === String(orgData?.Id) ? orgData?.OrganizationName : '-';
    const phoneDisplay = isSupervisor ? (supervisorInfo?.phone || '-') : (
        <>
            {userView?.phone || '-'}
            <EditButton onClick={() => setIsEditPhone(true)} testId="details editPhone" ariaLabel="Edit phone" />
        </>
    );
    const emailDisplay = () => {
        const person = isSupervisor ? prevSupervisorInfo : userView;
        const isEmailVerified = person?.isEmailVerified ?? person?.is_email_verified;
      
        return (
            <>
                {person?.email || '-'}
                {!isEmailVerified && (
                    <small className="infoPending stickInfoBanner alignMiddle">
                        <Icon name="info" className="icon mr greyInfoIcon" /> Verification Pending
                    </small>
                )}
            </>
        );
    }
   
    // Effects
    useEffect(() => {
        if (!userView) return;
        setUserName({
            first:  userView?.firstName  || '',
            middle: userView?.middleName || '',
            last:   userView?.lastName   || '',
        });
        setPhone(userView?.phone || '');
        setSelectedTimeZone(getSelectedTZDisplayValue(userView?.timezone, timezoneData) || '');
        setHasUserImage(hasGuidValue(userView?.profileImage))
    }, [userView, timezoneData]);

    useEffect(() => {
        if (userView?.supervisorId) {
            dispatch(getManageUserList(userView.supervisorId));
        }
    }, [userView?.supervisorId, dispatch]);

    // Validation
    const validatePhone = () => {
        let error = null;
        if (!phone?.trim()) {
            error = "Phone number is required.";
        } else if (!PHONE_REGEX.test(phone)) {
            error = "Please enter valid phone number.";
        }
        setValidationErrors(prev => ({ ...prev, phone: error }));
        return !error;
    };

    const validateUserName = () => {
        const errors  = { ...EMPTY_NAME_ERRORS, phone: validationErrors.phone };
        let isValid   = true;
        const nameErr = "Only letters, spaces, hyphens and apostrophes are allowed.";

        if (!userName.first.trim()) {
            errors.first = "First Name is required."; isValid = false;
        } else if (!NAME_REGEX.test(userName.first)) {
            errors.first = nameErr; isValid = false;
        }
        if (!userName.last.trim()) {
            errors.last = "Last Name is required."; isValid = false;
        } else if (!NAME_REGEX.test(userName.last)) {
            errors.last = nameErr; isValid = false;
        }
        if (userName.middle && !NAME_REGEX.test(userName.middle)) {
            errors.middle = nameErr; isValid = false;
        }

        setValidationErrors(errors);
        return isValid;
    };

    const basePayload = useMemo(() => {
        const payload = {
            "user":{
                "first_name": userView?.firstName,
                "last_name" : userView?.lastName,
                "middle_name": userView?.middleName,
                "email"     : userView?.email,
                "user_name" : userView?.username,
                "user_role" : userView?.userRole,
                "phone"     : userView?.phone,
                "job_role"  : userView?.job_role,
                "supervisor_id" : userView?.supervisorId ? Number(userView.supervisorId) : null,
                "profile_image": userView?.profileImage,
                "userId"    : userView?.id // Used for ApiCall
            }
        }  
        return payload;
    }, [userView]);

    // Handlers
    const updateUserName = () => {
        if (!validateUserName()) return;
        const newPayload = {
            ...basePayload,
            user: {
                ...basePayload.user,
                first_name  : userName.first.trim(),
                last_name   : userName.last.trim(),
                middle_name : userName.middle.trim()
            }
        }
        dispatch(updateUserData(newPayload));
        setIsEditName(false);
    };

    const updatePhone = () => {
        if (!validatePhone()) return;
        const newPayload = {
            ...basePayload,
            user: {
                ...basePayload.user,
                phone   : phone.trim()
            }
        }
        dispatch(updateUserData(newPayload));
        setIsEditPhone(false);
    };

    const hideEditName = () => {
        setUserName({
            first:  userView?.firstName  || '',
            middle: userView?.middleName || '',
            last:   userView?.lastName   || '',
        });
        setValidationErrors(prev => ({ ...prev, ...EMPTY_NAME_ERRORS }));
        setIsEditName(false);
    };

    const hideEditPhone = () => {
        setPhone(userView?.phone || '');
        setValidationErrors(prev => ({ ...prev, phone: null }));
        setIsEditPhone(false);
    };

    const hideUserTimezone = () => {
        setSelectedTimeZone(getSelectedTZDisplayValue(userView?.timezone, timezoneData) || '');
        setIsEditTimezone(false);
    };

    const onImageSuccess = (result) => {
        const { guid, file_name } = result;
        if (guid) {
            const extension = file_name.split('.').pop();
            const newPayload = {
                ...basePayload,
                user: {
                    ...basePayload.user,
                    profile_image : `${guid}.${extension}`,
                }
            }
            dispatch(updateUserData(newPayload));
        }
        setIsModal(false);
    };

    const onImageRemove = () => {
        const newPayload = {
            ...basePayload,
            user: {
                ...basePayload.user,
                profile_image : null,
            }
        }
        dispatch(updateUserData(newPayload));
        setIsConfirmation(false);
    };

    const updateTimeZone = () => {
        const selectedTz = selectedTimeZone ? timezoneData.find(t => t.display_value === selectedTimeZone) : null;
        const tzId = selectedTz?.id;
        if (tzId && userView?.TimeZoneTz !== tzId) {
            const newPayload = {
                ...basePayload,
                user: {
                    ...basePayload.user,
                    timeZoneId      : tzId,
                    timeZoneValue   : selectedTz.standard_time_zone,
                    mode            : "UserTimezone",
                }
            }
            dispatch(updateUserData(newPayload));
            showToastMessage();
        }
        setIsEditTimezone(false);
    };

    const handleBreadcrumbClick = () => {
        setIsSupervisor(false);
        dispatch(getManageUserList(userView?.supervisorId));
    };

    const handleSupervisorClick = (data) => {
        if (data?.supervisor_id) {
            setPrevSupervisorInfo(supervisorInfo);
            setIsSupervisor(true);
            dispatch(getManageUserList(data.supervisor_id));
        }
    };

    // Early return
    if (isLoading || !userView) return <div className="companyList__profileBox scrollArea"><LoaderComponent /></div>;
    
    return (
        <>
            <DialogueBox
                overlayClassName="modal"
                contentClassName="modalContent animated faster fadeInUpx2 small"
                shouldCloseOnOverlayClick={false}
                isOpen={isModal || isConfirmation}
                onRequestClose={() => { setIsModal(false); setIsConfirmation(false); }}
            >
                {isConfirmation ? (
                    <ConfirmPrompt
                        message="Do you really want to remove your profile image?"
                        onCancelClick={() => setIsConfirmation(false)}
                        onRemoveClick={onImageRemove}
                        isLogoFooter
                        class="modalBody"
                        removeButtonText="Remove"
                        footerClass="modalFooter"
                        cancelText="Cancel"
                    />
                ) : (
                    <ImageCropper
                        wrapperCls="row_block flexGrow"
                        uploadText="Upload"
                        title="Upload Profile Image"
                        onClose={() => setIsModal(false)}
                        onSuccess={onImageSuccess}
                        moduleType="user"
                    />
                )}
            </DialogueBox>

            <div className="companyList__profileBox scrollArea">

                {/* ── Header ── */}
                <div className="companyList__profileBox__widgets">
                    {isSupervisor ? (
                        <ul className="rowFlex alignMiddle">
                            <button type="button" className="btn-cc btn-cc__transparent btn-cc btn-cc__link__blue" onClick={handleBreadcrumbClick}>
                                Details
                            </button>
                            <span className="pointer black ml"> / {buildFullName(prevSupervisorInfo)}</span>
                        </ul>
                    ) : (
                        <strong className="flexGrow">Details</strong>
                    )}
                </div>

                {/* ── Profile image ── */}
                {isSupervisor ? (
                    <div className="companyList__profileBox__profileData" data-test="details userImage">
                        <span className="rowFlex alignMiddle">
                            <div className="mr__1">
                                <UserImage
                                    profileUrl={prevSupervisorInfo.profile_image}
                                    className="profilePic"
                                    profileName={buildFullName(prevSupervisorInfo)}
                                />
                            </div>
                            <div className="colFlex line-height__1">
                                <span>{buildFullName(prevSupervisorInfo)}</span>
                                <label>{prevSupervisorInfo.title}</label>
                            </div>
                        </span>
                    </div>
                ) : (
                    <div className="companyList__profileBox__profileData" data-test="details userImage">
                        <label className="gridEven alignMiddle">User Image</label>
                        <span className="gridOdd rowFlex alignMiddle">
                            <div className="mr__1">
                                <UserImage
                                    profileUrl={userView?.profileImage}
                                    className="profilePic"
                                    profileName={`${userView.firstName} ${userView.lastName}`}
                                />
                            </div>
                            <div className="colFlex line-height__1">
                                <ul className="rowFlex">
                                    <span className="mr">
                                    <button type="button" className="btn-cc btn-cc__transparent btn-cc btn-cc__link__blue" onClick={() => setIsModal(true)}>
                                        {hasUserImage ? 'Change' : 'Upload'}
                                    </button></span>
                                    {hasUserImage && (
                                        <button type="button" className="btn-cc btn-cc__transparent btn-cc btn-cc__link__blue" onClick={() => setIsConfirmation(true)}>
                                            Remove
                                        </button>
                                    )}
                                </ul>
                                <span className="mt">{!hasUserImage && <label>Set a profile picture</label>}</span>
                            </div>
                        </span>
                    </div>
                )}

                {/* ── Own profile fields ── */}
                {!isSupervisor && (
                    <>
                        {/* Full Name */}
                        <ProfileRow label="Full Name" testId="details userName">
                            {isEditName ? (
                                <ul className="fieldName rowFlex">
                                    {NAME_FIELDS.map(field => (
                                        <ValidationField key={field} error={validationErrors[field]}>
                                            <input
                                                type="text"
                                                maxLength={150}
                                                value={userName[field] ?? ''}
                                                onChange={e => setUserName(prev => ({ ...prev, [field]: e.target.value }))}
                                                onFocus={() => setValidationErrors(prev => ({ ...prev, [field]: null }))}
                                            />
                                        </ValidationField>
                                    ))}
                                    <EditActions onSave={updateUserName} onCancel={hideEditName} />
                                </ul>
                            ) : (
                                <>
                                    {buildFullName(userView)}
                                    <EditButton onClick={() => setIsEditName(true)} testId="details editName" ariaLabel="Edit name" />
                                </>
                            )}
                        </ProfileRow>

                        <ProfileRow label="Job Title" testId="details jobTitle">
                            {userView?.title || '-'}
                        </ProfileRow>
                    </>
                )}

                {/* ── Common fields ── */}
                <ProfileRow label="Company Name" testId="details companyName">
                    {companyName}
                </ProfileRow>

                <ProfileRow label="Phone" testId="details phone">
                    {isEditPhone ? (
                        <ul className="fieldPhone rowFlex wth-5">
                            <ValidationField error={validationErrors.phone}>
                                <input
                                    type="text"
                                    value={phone}
                                    maxLength={20}
                                    onChange={e => setPhone(e.target.value)}
                                    onFocus={() => setValidationErrors(prev => ({ ...prev, phone: null }))}
                                />
                            </ValidationField>
                            <EditActions onSave={updatePhone} onCancel={hideEditPhone} />
                        </ul>
                    ) : phoneDisplay}
                </ProfileRow>

                <div className="companyList__profileBox__profileData" data-test="details email">
                    <label className="gridEven">Email</label>
                    <span className="gridOdd alignMiddle">
                        {emailDisplay()}
                    </span>
                </div>

                <ProfileRow label="Job Role" testId="details JobRole">
                    {isSupervisor ? (supervisorInfo?.job_role || '-') : (userView?.job_role || '-')}
                </ProfileRow>

                {/* ── License + Timezone (own profile only) ── */}
                {!isSupervisor && (
                    <>
                        {isModuleEnabled("manage_profile.details.license_number") && <div className="companyList__profileBox__profileData">
                            <label className="gridEven">Adjuster License Number</label>
                            <div className="colFlex licenceNumber">
                                {userView?.license?.length > 0 ? (
                                    <>
                                        <ul className="licenceHeading alignMiddle">
                                            <li>State</li>
                                            <li>License Number</li>
                                            <li>License Expiration Date</li>
                                        </ul>
                                        {userView?.license.map(item => (
                                            <ul className="alignMiddle" key={`${item.state}-${item.license_number}`}>
                                                <li><span>{item.state}</span></li>
                                                <li><span>{item.license_number}</span></li>
                                                <li><span>{moment(item.expiration_date).format(DEFAULT_DATE_FORMAT)}</span></li>
                                            </ul>
                                        ))}
                                    </>
                                ) : '-'}
                            </div>
                        </div>}

                        <ProfileRow label="Time Zone" testId="details timezone">
                            {isEditTimezone ?
                                <div className="rowFlex alignMiddle">
                                    <div className="formGroup wth-5 mr">
                                        <select value={selectedTimeZone} onChange={e => setSelectedTimeZone(e.target.value)}>
                                            {timezoneData.map(tz => (
                                                <option key={tz.id} value={tz.display_value}>
                                                    {tz.display_value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <EditActions onSave={updateTimeZone} onCancel={hideUserTimezone} />
                                </div>
                            :
                            <>
                                {selectedTimeZone}
                                <EditButton onClick={() => setIsEditTimezone(true)} testId="details editTimezone" ariaLabel="Edit time zone" />
                            </>
                            }
                        </ProfileRow>
                    </>
                )}

                {/* ── Supervisor ── */}
                {hasSupervisor(supervisorInfo) && (
                    <div className="companyList__profileBox__profileData" data-test="details supervisorName">
                        <label className="gridEven alignMiddle">Supervisor</label>
                        <span className="rowFlex gridOdd alignMiddle">
                            <div className="mr">
                                <UserImage profileUrl="default_user_s.svg" profileName={buildFullName(supervisorInfo)} />
                            </div>
                            <button type="button" className="btn-cc btn-cc__transparent btn-cc btn-cc__link__blue" onClick={() => handleSupervisorClick(supervisorInfo)}>
                                {buildFullName(supervisorInfo)}
                            </button>
                        </span>
                    </div>
                )}
            </div>
        </>
    );
};

export default Details;